import {
  useMutation,
  UseMutationResult,
  useQuery,
} from "@tanstack/react-query";
import { queryKeys } from "../constants";
import {
  getCustomers,
  getCustomerById,
  createCustomer,
  updateCustomer,
  deleteCustomer,
} from "services";
import { handleAPIError, queryClient } from "utils";
import { CustomerType } from "types";
import { toast } from "react-toastify";

// Fetch all customers
export const useGetCustomers = (branch_id?: string | number) => {
  return useQuery({
    queryKey: [queryKeys.customers, branch_id],
    queryFn: async () => await getCustomers(branch_id),
  });
};

// Fetch a single customer by ID
export const useGetCustomerById = (id: string) => {
  return useQuery({
    queryKey: [queryKeys.customers, id], // Unique key for fetching a single customer
    queryFn: async () => await getCustomerById(id),
    enabled: !!id, // Only run query if ID is provided
  });
};

// Create a new customer
export const useCreateCustomer = () => {
  return useMutation({
    mutationFn: createCustomer,
    onSuccess: async () => {
      toast.success("Customer added successfully");
      // Invalidate customers query on success to refresh the list
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.customers],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};

// Define the UpdateCustomerPayload type
export type UpdateCustomerPayload = {
  id: string; // Required customer ID
  data: CustomerType; // New customer data to update
};

// Hook to update a customer
export const useUpdateCustomer = (): UseMutationResult<
  any,
  Error,
  UpdateCustomerPayload
> => {
  return useMutation<any, Error, UpdateCustomerPayload>({
    mutationFn: async ({ id, data }) => {
      return updateCustomer(id, data); // Ensure this function matches your API
    },
    onSuccess: async () => {
      toast.success("Customer info updated successfully");
      // Invalidate customers query on success
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.customers],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};

// Delete a customer
export const useDeleteCustomer = () => {
  return useMutation({
    mutationFn: deleteCustomer,
    onSuccess: async () => {
      toast.success("Custom info deleted successfully.");
      // Invalidate customers query on success
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.customers],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};
